import { useState } from 'react';
import { useExtensions } from '../../lib/api/hooks.js';
import {
  useRingGroups, useQueues, useIvrs, useMessages, useHoursChecks, useMailboxes, useCallerChecks, useLookups, useInboundRoutes,
} from '../../lib/api/hooks-tms.js';
import { Icon } from '../../components/ui/Icon.js';
import { FlowChartViewer } from './flowchart.js';
import { buildRouteTree, type FlowRoute, type Lookup } from './routeTree.js';

export type { FlowRoute } from './routeTree.js';

/**
 * What a route does, drawn (docs/26 D1): the route being edited, as a flowchart.
 *
 * Drawn from what is being edited, before it is saved, so a change shows its
 * consequence while it can still be undone.
 *
 * Its third look. An indented cascade, then a tree of boxes; the owner wanted a
 * flowchart like Mermaid's, with decision points for the hours and for an IVR,
 * in a window of its own to zoom and print (14 September 2026). Drawn when asked
 * for, from a button: always on the page, a real menu's chart took over the
 * form it explains. The tree comes from routeTree.ts and the drawing from
 * flowchart.tsx; this only fetches what the route points at.
 */
export function RouteFlow({ tenantId, route, title }: {
  tenantId: string;
  route: FlowRoute;
  /** The route's name, for the window's title and the printout. */
  title?: string | undefined;
}) {
  const [open, setOpen] = useState(false);
  const extensions = useExtensions(tenantId, { limit: 1000 });
  const groups = useRingGroups(tenantId);
  const queues = useQueues(tenantId);
  const ivrs = useIvrs(tenantId);
  const messages = useMessages(tenantId);
  const hours = useHoursChecks(tenantId);
  const boxes = useMailboxes(tenantId);
  const callers = useCallerChecks(tenantId);
  const lookups = useLookups(tenantId);
  const flows = useInboundRoutes(tenantId);

  const data: Lookup = {
    ext: new Map((extensions.data?.data ?? []).map((e) => [e.id, e])),
    groups: new Map((groups.data?.data ?? []).map((g) => [g.id, g])),
    queues: new Map((queues.data?.data ?? []).map((q) => [q.id, q])),
    ivrs: new Map((ivrs.data?.data ?? []).map((m) => [m.id, m])),
    messages: new Map((messages.data?.data ?? []).map((m) => [m.id, m])),
    hours: new Map((hours.data?.data ?? []).map((h) => [h.id, h])),
    callers: new Map((callers.data?.data ?? []).map((c) => [c.id, c])),
    lookups: new Map((lookups.data?.data ?? []).map((l) => [l.id, l])),
    flows: new Map((flows.data?.data ?? []).map((r) => [r.id, r])),
    boxes: new Map((boxes.data?.data ?? []).map((b) => [b.id, b])),
  };
  // Built only while the window is open: nothing is drawn that nobody asked for.
  const root = open ? buildRouteTree(route, data) : null;
  const label = route.numbers.length > 0
    ? `What happens to a call to ${route.numbers.join(', ')}`
    : 'What happens to a call on this route';

  return (
    <>
      <button type="button" className="arc-btn arc-btn-sm" onClick={() => setOpen(true)}>
        <Icon name="ivr" size={13} /> Show flow diagram
      </button>
      {root && (
        <FlowChartViewer root={root} label={label}
          title={title ? `What happens · ${title}` : 'What happens'}
          onClose={() => setOpen(false)} />
      )}
    </>
  );
}
